import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { GPC_CATEGORIES_TABLE, GPC_LOCALE_OPTIONS } from '../constants/gpc-categories';
import { toGpcCategory } from '../mappers/gpc-categories.mapper';
import { fetchConfigTableRows } from '../services/config-table.service';
import type { GpcCategory } from '../types/gpc-categories.types';

/**
 * Every locale's spelling of one `gpc_id`.
 *
 * This is the question the table exists to answer: `gpc_id` is the locale-invariant
 * identity, and 1,740 of the 5,595 ids (31%) have *different* path text between
 * `en-US` and `en-GB`. So a row on its own is half the answer — the detail panel
 * shows the other half.
 *
 * One request, not one per locale: `gpc_id eq` matches every locale row, and the
 * page size tracks the known vocabulary so a third locale would be picked up rather
 * than silently truncated.
 *
 * `staleTime: Infinity` — reference data mirrored from a versioned upstream file; it
 * cannot change while the panel is open.
 */
export function useGpcCategoryLocales(gpcId: number | null) {
  return useQuery({
    // `gpcId ?? -1` never reaches the network: `enabled` is false when it is null.
    // The key still needs a value, and no real gpc_id is negative.
    queryKey: queryKeys.configuration.gpcCategoryLocales(gpcId ?? -1),
    queryFn: () => {
      // Unreachable while `enabled` is false — a guard rather than a non-null
      // assertion, so a future change to `enabled` fails loudly instead of sending
      // `value: null` to an integer column.
      if (gpcId === null) throw new Error('useGpcCategoryLocales: called with no gpc_id');

      return fetchConfigTableRows({
        tableName: GPC_CATEGORIES_TABLE,
        query: { filters: [{ column: 'gpc_id', op: 'eq', value: gpcId }] },
        page: 1,
        size: GPC_LOCALE_OPTIONS.length,
        sortBy: 'locale',
        sortOrder: 'ASC',
        mapRow: toGpcCategory,
      });
    },
    enabled: gpcId !== null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * The rows for every locale *other* than the one being viewed.
 *
 * Derived rather than filtered server-side with `locale ne`: the same request then
 * serves any row of the id, so opening the en-US row and then the en-GB row of the
 * same category is one network call, not two.
 */
export function otherLocaleRows(rows: GpcCategory[] | undefined, locale: string): GpcCategory[] {
  return (rows ?? []).filter((row) => row.locale !== locale);
}
